import {useEffect, useState} from 'react';

import Box from '@mui/material/Box';
import muiStyled from '@mui/material/styles/styled';
import {useWindowSize} from '@uidotdev/usehooks';
import {models} from 'powerbi-client';
import {useLocation} from 'react-router-dom';
import styled from 'styled-components';

import EmbedReport from '../../components/embed/EmbedReport';
import {EMBED_REPORT_DEFAULT_SETTINGS as defaultSettings} from '../../const';
import useSaveViewBeforeLeavingReport from '../../hooks/components/main-sidebar/useSaveViewBeforeLeavingReport';
import usePersistReportSelection from '../../hooks/components/report/usePersistReportSelection';
import useReportTokenRefresh from '../../hooks/components/report/useReportTokenRefresh';
import useResponsiveReportSettings from '../../hooks/components/report/useResponsiveReportSettings';
import useChatbot from '../../hooks/providers/useChatbot';
import useProjects from '../../hooks/providers/useProjects';
import useReport from '../../hooks/providers/useReport';
import useWorkspaces from '../../hooks/providers/useWorkspaces';
import {getCompanyReportTheme} from '../../utils';

const PageContainer = muiStyled(Box)(() => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

const EmbedContainer = styled.div`
  max-height: ${props => props.hidden && 0};
  overflow: ${props => props.hidden && 'hidden'};
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  background-color: transparent;
  height: calc(100vh - 64px);
  width: 100%;
`;

const Report = () => {
  const {setReportSelectedFoldersSirens, tokenExpiration, setTokenExpiration, selectedReport, loadReports, reports, embedToken, setSelectedProject, reportSelectedPage, isReportLoaded} = useReport();
  const {isChatbotSidebarOpen} = useChatbot();
  const {projects} = useProjects();
  const {getWorkspaces, workspacesFetched} = useWorkspaces();

  const windowSize = useWindowSize();
  const location = useLocation();

  const projectSiren = location.state?.projectSiren || localStorage.getItem('projectSiren');
  const reportId = location.state?.reportId || localStorage.getItem('reportId');

  useSaveViewBeforeLeavingReport();

  usePersistReportSelection({reportId, projectSiren, location, setSelectedProject, setReportSelectedFoldersSirens, projects});
  const {reportSettings, pageBeforeReload} = useResponsiveReportSettings({windowSize, isChatbotSidebarOpen, selectedReport, reportSelectedPage});
  useReportTokenRefresh({report: reports?.find(r => r.report_id === reportId), selectedReport, tokenExpiration, loadReports, setTokenExpiration});

  const report = reports?.find(r => r.report_id === reportId);
  const isReportPage = location.pathname.startsWith('/reports');

  const commonReportConfigProps = {
    // theme: {themeJson: reportTheme},
    filters: null,
    type: 'report',
    id: report ? report.report_id : undefined,
    embedUrl: report ? report?.embed_url : undefined,
    accessToken: report ? embedToken : undefined,
    tokenType: models.TokenType.Embed
  };

  useEffect(() => {
    (async () => {
      if (isReportLoaded) {
        const reportTheme = await getCompanyReportTheme(report?.isCustomModel, selectedReport);
        try {
          await selectedReport.applyTheme({
            themeJson: reportTheme
          });
        } catch (e) {
          console.log({e});
        }
      }
    })();
  }, [isReportLoaded]);

  useEffect(() => {
    (async () => {
      if (!workspacesFetched && isReportPage) {
        console.log('Will call from here');
        await getWorkspaces();
      }
    })();
  }, []);

  return (
    <PageContainer>
      <EmbedContainer data-testid="embed-container" hidden={!location.pathname.startsWith('/reports')}>
        {reportSettings?.layoutType === models.LayoutType.MobilePortrait && (
          <EmbedReport
            updateToken={() => loadReports()}
            isCustomReport={report?.isCustomModel}
            schemas={report?.schema_name}
            lastPageBeforeReload={pageBeforeReload}
            reportConfig={{
              ...commonReportConfigProps,
              permissions: models.Permissions.ReadWrite,
              settings: {...defaultSettings, layoutType: models.LayoutType.MobilePortrait}
            }}
          />
        )}

        {reportSettings?.layoutType === models.LayoutType.Master && (
          <EmbedReport
            updateToken={() => loadReports()}
            isCustomReport={report?.isCustomModel}
            schemas={report?.schema_name}
            lastPageBeforeReload={pageBeforeReload}
            reportConfig={{
              ...commonReportConfigProps,
              permissions: models.Permissions.ReadWrite,
              settings: {...defaultSettings}
            }}
          />
        )}
      </EmbedContainer>
    </PageContainer>
  );
};

export default Report;
